'use server';

import { SessionService } from '@/contexts/authentication';
import type { Character } from '../types';
import { localizedTemplateName, systemTemplateId } from '../utils/systemCharacter';

export async function getCharacters(
  role?: string,
  scope: 'library' | 'story' | 'all' = 'library',
): Promise<Character[]> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return [];

  const { getCharacterRepository } = await import('@/contexts/characters/repositories');
  const characters = await getCharacterRepository().listByFamily({
    familyId,
    role: role || undefined,
    limit: 50,
    scope,
  });

  // The shared heroes were copied into the library under whatever name the
  // template carried at the time (Russian, for every family created before the
  // names were localized). They are admin-managed, so their display name follows
  // the reader's language rather than that historical copy.
  const systemIds = characters
    .map((c) => systemTemplateId(c.id))
    .filter((id): id is string => !!id);
  if (!systemIds.length) return characters as Character[];

  const { getLocale } = await import('next-intl/server');
  const { getCharacterTemplateRepository } = await import('@/contexts/characters/repositories');
  const locale = await getLocale().catch(() => 'en');
  const templates = await getCharacterTemplateRepository().listAllTemplates();
  const byId = new Map(templates.map((t) => [t.id, t]));

  return characters.map((c) => {
    const tpl = byId.get(systemTemplateId(c.id) ?? '');
    return tpl ? { ...c, name: localizedTemplateName(tpl, locale) } : c;
  }) as Character[];
}
